import { animate } from "motion";
import { type ReactNode, useLayoutEffect, useRef } from "react";

import { spring } from "./motion.js";

type Props = {
  children: ReactNode;
  /** リスト内の位置。わずかにずらして出すと、並びの順序が読み取りやすくなる。 */
  index?: number;
  className?: string;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * 出現アニメーション。
 *
 * 通常はごくわずかに下から持ち上げつつフェードインする。動かすのは transform と opacity
 * だけなのでコンポジタから外れない。
 *
 * モーションを減らす設定では移動を完全に落とし、短いクロスフェードだけにする
 * (動きを消すのではなく、前庭感覚に障らない等価物に置き換える)。
 */
export function Reveal({ children, index = 0, className }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  // delay の算出にだけ使う。index を effect の依存に入れると、並び替えで index が
  // 変わるたびに再生され、既に見えているカードが opacity 0 に戻って光る。
  const indexRef = useRef(index);
  indexRef.current = index;

  // 依存は空。この要素が DOM に現れた 1 回だけ再生する。
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const i = indexRef.current;

    if (prefersReducedMotion()) {
      void animate(el, { opacity: [0, 1] }, { duration: 0.2, delay: Math.min(i, 6) * 0.015 });
      return;
    }

    void animate(
      el,
      { opacity: [0, 1], y: [8, 0] },
      // 勢いを伴わない出現なので跳ねさせない (damping 1.0)。
      { ...spring.default, delay: Math.min(i, 8) * 0.03 },
    );
  }, []);

  return (
    <div ref={ref} className={className} style={{ opacity: 0 }}>
      {children}
    </div>
  );
}
